import { EntityDescriptor, Optional } from "@crispico/foundation-react";
import { Filter } from "@crispico/foundation-react/apollo-gen-foundation/Filter";
import { Organization } from "@crispico/foundation-react/AppMeta";
import { CalculateForRecordsWidgetConfig } from "@crispico/foundation-react/components/CalculateForRecords/CalculateForRecords";
import { ErrorComponent } from "@crispico/foundation-react/components/ErrorComponent/ErrorComponent";
import { ModalExt } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { DispatchersFrom } from "@crispico/foundation-react/reduxHelpers";
import React, { RefObject } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { Button, Icon, Modal, Popup, Segment, SemanticICONS } from "semantic-ui-react";
import { DashboardMode } from "../DashboardContants";
import { Dashboard } from "../DashboardEntityDescriptor";
import { DashboardWidgetFactory } from "../DashboardWidgetFactory";
import { DashboardTab } from "./DashboardTab";
import { createTestids } from "@famiprog-foundation/tests-are-demo";

export const widgetWrapperTestids = createTestids("WidgetWrapper", {
    deleteConfirmation: "",
});

export type WidgetConfig = { 
    title?: string, 
    description?: string, 
    headerFontSize?: string, 
    headerBackgroundColor?: string, 
    headerIcon?: SemanticICONS
    backgroundColor?: string
 };

type WidgetWrapperProps<T = WidgetConfig> = {
    id: string,
    ref?: RefObject<any>,
    widgetId: string,
    mode: DashboardMode,
    factory: DashboardWidgetFactory,
    widgetConfig: T,
    organization?: Optional<Organization>,
    editorOpen: string | undefined,
    dashboardTabRef: React.RefObject<DashboardTab>,
    dashboardEntity: Dashboard,
    entityForAttachedDashboard: any,
    zeroTrainingMode?: boolean,
    deleteModalOpen: string | undefined,
    refresh: boolean,
    dataExplorerFilter: Filter | undefined,
    dataExplorerEntityDescriptor: EntityDescriptor | undefined
}

export type WidgetProps<T = WidgetConfig> = WidgetWrapperProps<T> & {
    /**
     * Missing when "add wizard" mode.
     */
    buttonBarRef?: RefObject<HTMLElement>;
};

export class WidgetWrapper extends React.Component<WidgetWrapperProps> {

    titleBarRef: RefObject<HTMLElement>;
    buttonBarRef: RefObject<HTMLElement>;
    widgetRef: RefObject<any>;

    state = {
        tooltipNeeded: false
    }

    constructor(props: WidgetWrapperProps) {
        super(props)
        this.titleBarRef = React.createRef();
        this.buttonBarRef = React.createRef();
        this.widgetRef = React.createRef();
    }

    componentDidUpdate(prevProps: WidgetWrapperProps) {
        this.componentDidUpdateInternal(prevProps);
    }

    componentDidUpdateInternal(prevProps?: WidgetWrapperProps) {
        if (prevProps && prevProps.refresh !== this.props.refresh){
            this.widgetRef.current.refresh();
        }
    }

    componentDidMount() {
        if (this.titleBarRef.current && this.titleBarRef.current.scrollWidth > this.titleBarRef.current.clientWidth) {
            this.setState({ tooltipNeeded: true })
        }
    }

    render() {
        const props = this.props;
        const visibility = props.mode === DashboardMode.VIEW ? 'hidden' : 'visible';
        const widgetConfig: WidgetConfig = props.widgetConfig;

        const title = props.factory.renderTitle(widgetConfig);
        const headerFontSize = widgetConfig.headerFontSize ? widgetConfig.headerFontSize : props.dashboardEntity.widgetHeaderFontSize;
        const headerBackgroundColor = widgetConfig.headerBackgroundColor ? widgetConfig.headerBackgroundColor : props.dashboardEntity.widgetHeaderBackgroundColor;
        const headerIcon: SemanticICONS | undefined = widgetConfig.headerIcon ? widgetConfig.headerIcon : props.dashboardEntity.widgetHeaderIcon;
        const isWidgetWrapperVisible = props.factory.isWidgetWrapperVisible(widgetConfig);

        let widgetHeaderStyle: any = {
            backgroundColor: headerBackgroundColor
        };
        if (title) {
            widgetHeaderStyle = {...widgetHeaderStyle, ...{
                alignItems: "center", padding: "5px 0px"
            }}
        } else if (DashboardMode.EDIT && (!title || title.length === 0)) {
            widgetHeaderStyle = {...widgetHeaderStyle, ...{
                position: "absolute", width: "100%", padding: "5px 10px", zIndex: 1000
            }}
        }

        return <>
                <Segment basic={isWidgetWrapperVisible || this.props.mode === DashboardMode.EDIT ? false : true} style={{ height: '100%', background: widgetConfig.backgroundColor }} className="flex-container flex-grow flex-shrink-auto" data-cy="Dashboard.widget" key={props.id}>
                    {props.mode === DashboardMode.EDIT || (title && title.length > 0) ?
                        <Segment basic className='flex-container-row no-margin flex-shrink-auto' style={{...widgetHeaderStyle}}>
                            <Popup wide position="bottom center"
                                content={title}
                                disabled={!this.state.tooltipNeeded}
                                trigger={<div className='white-space-normal flex-shrink-auto WidgetHeaderTitle' style={{ fontSize: headerFontSize }} ref={this.titleBarRef as RefObject<HTMLDivElement>}>
                                    {headerIcon ? <Icon name={headerIcon} /> : null} {title}
                                </div>}
                            />
                            <div className="flex-shrink-auto">
                                <span ref={this.buttonBarRef} />
                                {widgetConfig.description ? <Popup wide position="bottom center" popperModifiers={[{ name: "preventOverflow", options: { boundariesElement: "offsetParent" } }]}
                                    content={widgetConfig.description}
                                    trigger={<Icon link name="question" />}
                                /> : null}
                                {props.mode === DashboardMode.EDIT ?
                                    <>
                                        <Button role="edit" size="tiny" className='DashboardWidgetButton' style={{ visibility: visibility }} icon="settings"
                                            onClick={e => props.dashboardTabRef.current?.props.r.setInReduxState({ editorOpen: props.id })} data-cy="Dashboard.widget.button" />
                                        <Modal open={props.deleteModalOpen === props.id} closeOnDocumentClick closeOnEscape
                                            onOpen={() => props.dashboardTabRef.current?.props.r.setInReduxState({ deleteModalOpen: props.id })}
                                            onClose={() => props.dashboardTabRef.current?.props.r.setInReduxState({ deleteModalOpen: undefined })}
                                            trigger={<Button role="delete" negative size="tiny" className='DashboardWidgetButton' style={{ visibility: visibility }} icon="delete" />}>
                                            <Modal.Header>{_msg("Dashboard.widget.button.delete.messageHeader")}</Modal.Header>
                                            <Modal.Content>
                                                {_msg("Dashboard.widget.button.delete.messageContent")}
                                            </Modal.Content>
                                            <Modal.Actions>
                                                <Button negative onClick={() => props.dashboardTabRef.current?.props.r.setInReduxState({ deleteModalOpen: undefined })}>{_msg("general.no")}</Button>
                                                <Button data-testid={widgetWrapperTestids.deleteConfirmation} positive onClick={() => {
                                                    props.dashboardTabRef.current?.props.r.setInReduxState({ deleteModalOpen: undefined });
                                                    props.dashboardTabRef.current?.props.r.removeItem(props.id);
                                                }}>{_msg("general.yes")}</Button>
                                            </Modal.Actions>
                                        </Modal>
                                    </> : null}
                            </div>
                        </Segment> : null}
                    <ErrorBoundary FallbackComponent={(props: FallbackProps) => <ErrorComponent error={props.error} resetErrorBoundary={props.resetErrorBoundary} hintFixErrorMessage={_msg('Dashboard.widget.checkConfiguration')}/>}>
                        <div className={"flex-container flex-grow" + (props.mode === DashboardMode.EDIT ? " disabledItem" : "")} style={{ overflow: "auto" }} key="widget">
                            {props.factory.renderWidget(props.widgetId, { ...props, buttonBarRef: this.buttonBarRef, ref: this.widgetRef }, props.dashboardTabRef)}
                        </div>
                    </ErrorBoundary>
                </Segment>
            <ModalExt open={props.id === props.editorOpen} closeIcon={true} closeOnDimmerClick={true} onClose={() => props.dashboardTabRef.current?.props.r.setInReduxState({ editorOpen: undefined })} maxWidth={800}>
                <Modal.Header>{_msg('Dashboard.editor.title')}</Modal.Header>
                <Modal.Content>
                    {props.factory.renderEditor(props.id, stringifyWidgetConfig(props.factory, widgetConfig, props.dashboardEntity), props.dashboardTabRef, props.dashboardEntity)}
                </Modal.Content>
            </ModalExt>
        </>
    }
}

function stringifyWidgetConfig(dashboardWidgetFactory: DashboardWidgetFactory, widgetConfig: any, dashboardEntity: Dashboard) {
    let values: any = {};
    const fields = dashboardWidgetFactory.getEntityDescriptor(widgetConfig, dashboardEntity).fields;
    Object.keys(fields).forEach(f => {
        values[f] = fields[f].json && widgetConfig[f] ? JSON.stringify(widgetConfig[f]) : widgetConfig[f]
    })
    return values
}"../../..""../../../apollo-gen-foundation/Filter""../../../AppMeta""../../../components/CalculateForRecords/CalculateForRecords""../../../components/ErrorComponent/ErrorComponent""../../../components/ModalExt/ModalExt""../../../reduxHelpers"